import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, ArrowRight } from "lucide-react";
import Ornament, { SectionOverline } from "../components/Ornament";
import SmartImage from "../components/SmartImage";
import { api, BLOG_CATEGORIES, blogCategoryColor } from "../lib/config";
import useSEO from "../hooks/useSEO";

const BlogPage = () => {
  useSEO({
    title: "Blog",
    description: "Conseils et inspirations sur la gravure laser : techniques, idées cadeaux mariage, cadeaux d'entreprise, décoration en bois gravé, tutoriels d'entretien.",
  });
  const [posts, setPosts] = useState([]);
  const [cat, setCat] = useState("all");

  useEffect(() => {
    (async () => {
      const r = await api.get("/blog");
      setPosts(r.data);
    })();
  }, []);

  const filtered = cat === "all" ? posts : posts.filter(p => p.category === cat);

  return (
    <div data-testid="blog-page">
      <section className="pt-32 pb-12 bg-[#171717]">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <Ornament />
          <SectionOverline className="mt-6">Journal de l'atelier</SectionOverline>
          <h1 className="font-serif text-5xl sm:text-6xl text-white mt-4 mb-6">Inspirations & conseils</h1>
          <p className="text-base sm:text-lg text-[#DDD4B2]/80 max-w-2xl mx-auto">Techniques, idées cadeaux, tutoriels : partageons notre passion.</p>
        </div>
      </section>

      <section className="py-8 bg-[#171717] border-y border-[#9A8640]/20 sticky top-0 z-30 backdrop-blur-xl bg-[#171717]/85">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap gap-3 justify-center" data-testid="blog-filters">
          {BLOG_CATEGORIES.map((c) => (
            <button
              key={c.slug}
              onClick={() => setCat(c.slug)}
              className={`px-5 py-2 text-xs uppercase tracking-[0.18em] rounded-full border transition-all ${
                cat === c.slug ? "bg-[#9A8640] text-[#171717] border-[#9A8640]" : "text-[#DDD4B2]/80 border-[#9A8640]/40 hover:border-[#9A8640]"
              }`}
              data-testid={`blog-filter-${c.slug}`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </section>

      <section className="py-16 bg-[#171717]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {filtered.map((p, i) => (
              <motion.article
                key={p.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 4) * 0.1 }}
                className="group"
                data-testid={`blog-card-${p.id}`}
              >
                <Link to={`/blog/${p.slug}`}>
                <div className="aspect-[4/5] overflow-hidden mb-5 relative">
                    <SmartImage src={p.cover_image} alt={p.title} width={500} eager={i < 3} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <span
                      className="absolute top-4 left-4 px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-white rounded-full"
                      style={{ background: blogCategoryColor(p.category) }}
                    >
                      {BLOG_CATEGORIES.find(c => c.slug === p.category)?.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-[#DDD4B2]/60 mb-2">
                    <Calendar size={12} />
                    <span>{new Date(p.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</span>
                  </div>
                  <h3 className="font-serif text-2xl text-[#DDD4B2] group-hover:text-[#9A8640] transition-colors leading-tight mb-3">{p.title}</h3>
                  <p className="text-sm text-[#DDD4B2]/70 leading-relaxed line-clamp-2 mb-4">{p.excerpt}</p>
                  <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#9A8640] group-hover:gap-3 transition-all">
                    Lire la suite <ArrowRight size={14} />
                  </span>
                </Link>
              </motion.article>
            ))}
          </div>
          {filtered.length === 0 && (
            <p className="text-center text-[#DDD4B2]/60 py-20">Aucun article dans cette catégorie pour le moment.</p>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
